import axios from "axios";
import Qs from "qs";
import { $user } from "./UserFactory";
import { SET_IS_UNAUTHORIZED, SET_USER_LOGIN_TOKEN, store } from "../store";
import appConfig from "../config/appConfig";

const serializerConfig = {
  arrayFormat: "brackets",
  encode: false,
};

function getWebDeviceId() {
  const key = "kjuup_web_device_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
    localStorage.setItem(key, id);
  }
  return id;
}

function getFingerprintHeaders() {
  const headers = {
    "x-platform": "web",
    "x-device-id": getWebDeviceId(),
  };
  try {
    const lang = (localStorage.getItem("language") || "de").split("-")[0].toLowerCase();
    if (lang) headers["x-ui-language"] = lang;
  } catch (e) {}
  const devSecret = process.env.REACT_APP_DEV_SECRET;
  if (devSecret) {
    headers["x-dev-secret"] = devSecret;
  }
  return headers;
}

export async function handleRequest(request) {
  return request;
}

export function handleResponse(value) {
  return value;
}

export async function handleApiError(error) {
  if (axios.isCancel(error)) {
    console.log('----> api call canceled');
    throw error;
  }


  if (!error.response) {
    throw error;
  }

  if (error.response.status === 401 || error.response.status === 402) {
    return error.response;
  } else if (error.response.status === 500) {
    throw error;
  }
  else if (error.response.status === 400) {
    return error.response;
  }

  throw error;
}

// Shared axios instance - created once, reused across all calls
const _sharedClient = axios.create({
  baseURL: appConfig.API_URL,
  paramsSerializer: (paramObject) => Qs.stringify(paramObject, serializerConfig),
});
_sharedClient.interceptors.request.use(handleRequest);
_sharedClient.interceptors.response.use(handleResponse, handleApiError);

function _handleUnauthorized(status) {
  if (_suppressUnauthorized) return;
  if (status === 401 || status === 402) {
    store.dispatch({ type: SET_IS_UNAUTHORIZED, status: true });
  }
}

// A revoked session / device mismatch / structurally invalid token can never be
// recovered by refreshing -> the client must hard logout.
function _isHardFail(code) {
  return code === "SESSION_REVOKED" || code === "DEVICE_MISMATCH" || code === "INVALID_TOKEN";
}

// Single-flight access-token refresh shared by all callers (callAPI, callAPI2,
// and CrudFactory). Resolves to the new access token on success, else null.
// Keyed by the access token we started with so an in-flight refresh for the
// previous session cannot overwrite a newly applied impersonation token.
let _refreshPromise = null;
let _refreshForToken = null;
let _suppressUnauthorized = false;

export function setSuppressUnauthorized(value) {
  _suppressUnauthorized = Boolean(value);
}

export function refreshAccessToken() {
  const currentAccess = $user.getToken();
  if (_refreshPromise && _refreshForToken === currentAccess) return _refreshPromise;

  const tokenAtStart = currentAccess;
  _refreshForToken = tokenAtStart;
  _refreshPromise = (async () => {
    try {
      const refreshToken = $user.getRefreshToken ? $user.getRefreshToken() : null;

      // With a refresh token: standard refresh. Without one (legacy long-lived
      // session): send the still-valid access token so the backend upgrades it.
      const body = refreshToken ? { refreshToken } : {};
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...getFingerprintHeaders(),
      };
      if (tokenAtStart) headers.Authorization = `jwt ${tokenAtStart}`;

      const res = await axios.post(`${appConfig.API_URL}/auth/refresh`, body, { headers });
      const data = res && res.data;
      if (data && data.status === true && data.token) {
        // Session was replaced (e.g. admin impersonate) while we were in flight.
        if ($user.getToken() !== tokenAtStart) return null;
        $user.setToken(data.token);
        if (data.refreshToken && $user.setRefreshToken) {
          $user.setRefreshToken(data.refreshToken);
        }
        // Swap the token the socket layer uses too (mirrors tokenRotated).
        try { store.dispatch({ type: SET_USER_LOGIN_TOKEN, status: data.token }); } catch (_) {}
        return data.token;
      }
      return null;
    } catch (e) {
      console.log("[apiUtils] token refresh failed:", e?.response?.status, e?.message);
      return null;
    } finally {
      if (_refreshForToken === tokenAtStart) {
        _refreshPromise = null;
        _refreshForToken = null;
      }
    }
  })();

  return _refreshPromise;
}

function _buildConfig(path, params, method, data, options, headersObj) {
  const headers = {
    Accept: "application/json",
    Authorization: `jwt ${$user.getToken()}`,
    ...getFingerprintHeaders(),
    ...headersObj,
  };
  if (data instanceof FormData) {
    delete headers["Content-Type"];
  } else if (!headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  return {
    url: path,
    method: method,
    params: params,
    data: data,
    headers,
    ...options,
  };
}

// Performs a request through the shared client. On a refreshable 401/402 it
// transparently refreshes the token once and retries; on hard failures (or if
// refresh fails) it triggers the global auto-logout.
async function _requestWithRefresh(config) {
  let response;
  try {
    response = await _sharedClient.request(config);
  } catch (error) {
    console.error("API call error:", error);
    response = error?.response || {
      status: 500,
      data: { status: false, code: "NETWORK_ERROR", message: "Network error" },
    };
  }

  if (response.status === 401 || response.status === 402) {
    const code = response.data && response.data.code;
    if (!config._retry && !_isHardFail(code)) {
      config._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        config.headers = { ...config.headers, Authorization: `jwt ${newToken}` };
        return _requestWithRefresh(config);
      }
    }
    _handleUnauthorized(response.status);
  }

  return response;
}

function callAPI(path, params, method, data = null, options = {}, headersObj = {}) {
  return _requestWithRefresh(_buildConfig(path, params, method, data, options, headersObj));
}

function callAPI2(path, params, method, data = null, options = {}, headersObj = {}) {
  return _requestWithRefresh(_buildConfig(path, params, method, data, options, headersObj));
}
export { callAPI, callAPI2, getWebDeviceId, getFingerprintHeaders };